import {Base, CreateObject, PostBase, PostObjectResponse} from "./metadata";
import { StrategicPillar } from "../services/strategic-pillar.service";

export class VVPMActivity extends CreateObject {
  readonly id!: number;
  name__v!: string;
  filename__v!: string;
  document_number__v!: string;
  document_creation_date__v!: string;
  expiration_date__c!: string;
  status__v!: string;
  display_status!: string | null;
  days_until_expiration!: number | null;
  isVisible!: boolean;
  created_at!: string;
  updated_at!: string;
  
  // Strategic Pillars (manually taggable)
  strategicPillars: StrategicPillar[] = [];
  
  // Veeva Audiences (read-only, synced from Veeva PromoMats)
  target_audience__c: string[] = [];
  specialist__c: string[] = [];

  get abstractID(): string {
    return String(this.id);
  }

  get abstractDate(): string {
    // Use document creation date for timeline placement
    return this.document_creation_date__v.split("T")[0];
  }

  toPost(changes: VVPMActivity): VVPMActivityPost {
    return new VVPMActivityPost(this, changes);
  }

  // Helper to get the Veeva Vault link
  get veevaVaultLink(): string {
    return `https://roche-promomats.veevavault.com/ui/#doc_info/${this.id}`;
  }

  // Helper to get combined audiences (specialists + non-specialist target audiences)
  getCombinedAudiences(): string[] {
    const combined: string[] = [];
    
    // Add all specialists (these are the actual specialist names)
    if (this.specialist__c && this.specialist__c.length > 0) {
      combined.push(...this.specialist__c);
    }
    
    // Add target audiences, but only if they're NOT "Specialist"
    // (because specialists are already listed by name above)
    if (this.target_audience__c && this.target_audience__c.length > 0) {
      const nonSpecialistAudiences = this.target_audience__c.filter(
        audience => audience.toLowerCase() !== 'specialist'
      );
      combined.push(...nonSpecialistAudiences);
    }
    
    return combined;
  }

  static override parseFromPostResponse(post: VVPMActivityPostResponse): VVPMActivityPostResponse {
    post.updatedData = Object.assign(new VVPMActivity(), post.updatedData);
    
    // Parse strategic pillars if present
    if ((post.updatedData as any).strategicPillars) {
      (post.updatedData as any).strategicPillars = (post.updatedData as any).strategicPillars.map((p: any) => Object.assign({}, p));
    }
    
    // Parse Veeva audience arrays if present (read-only from Veeva)
    if ((post.updatedData as any).target_audience__c) {
      (post.updatedData as any).target_audience__c = [...(post.updatedData as any).target_audience__c];
    }
    if ((post.updatedData as any).specialist__c) {
      (post.updatedData as any).specialist__c = [...(post.updatedData as any).specialist__c];
    }
    
    return post;
  }
}

export interface VVPMActivityEndpointResponse {
  isOld: boolean;
  lastUpdate: string;
  vvpmActivities: Array<VVPMActivity>;
}

export const TEST_VVPM_ACTIVITY = Object.assign(new VVPMActivity(), {
  id: 1033106,
  name__v: "Brain Exchange – 4 – Brain Health – Video for Instagram (Option 1)",
  filename__v: "ROC-158257_ ECTRIMS 25_Brain Exchange 4 - Brain Health_V2 - Instagram 1.mp4",
  document_number__v: "M-XX-00021935",
  document_creation_date__v: "2025-10-05T22:00:00.000Z",
  expiration_date__c: "2026-10-07T22:00:00.000Z",
  status__v: "Approved for Distribution",
  display_status: "Approved",
  days_until_expiration: 365,
  isVisible: true,
  created_at: "2025-10-13T11:08:12.673Z",
  updated_at: "2025-10-13T11:08:12.673Z",
  target_audience__c: ["Specialist"],
  specialist__c: ["Neurologist", "MS Specialist"]
}) as VVPMActivity;

export class VVPMActivityPost extends PostBase {
  id: typeof VVPMActivity.prototype.id;
  isVisible: typeof VVPMActivity.prototype.isVisible | undefined;

  constructor(source: VVPMActivity, newValue: VVPMActivity) {
    super(source, newValue);
    
    if (source.id !== newValue.id) {
      throw Error("VVPMActivityPost: id cannot be overwritten");
    }

    this.id = source.id;
    this.isVisible = newValue.isVisible;
  }

  override getSourceWrapper(): { (post: VVPMActivityPostResponse): VVPMActivityPostResponse } {
    return VVPMActivity.parseFromPostResponse;
  }
}

export type VVPMActivityPostResponse = PostObjectResponse<VVPMActivity>;
